import { useEffect, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { host } from '~/utils/api'
import { notify } from '~/utils/notify'
import { getToken } from '~/utils/token'
import { v4 as uuidv4 } from 'uuid'

type ApiDataSuccess = {
  status: 'ok'
  url: string
}

type ApiDataErrorSize = {
  status: 'error'
  reason: 'too_big'
  maxSize: number
}

type ApiDataType = ApiDataSuccess | ApiDataErrorSize

export type Attachment = {
  uuid: string
  url: string
  mimetype: string
  name: string
  size: number
  uploading: boolean
  error: boolean
  icon: string
  isImage: boolean
}

export const uploadUseDropzone = (
  maxSize: number,
  path: string,
  onStart: (resp: Attachment) => void,
  onEnd: (resp: Attachment) => void,
  pasteElementRef?: React.MutableRefObject<HTMLElement | undefined>
) => {
  const [isDraggingOverWindow, setDraggingOverWindow] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    const dragOver = (e: DragEvent) => {
      e.preventDefault()

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = undefined
      } else {
        setDraggingOverWindow(true)
      }

      timeoutRef.current = setTimeout(() => {
        setDraggingOverWindow(false)
        timeoutRef.current = undefined
      }, 1000)
    }

    document.addEventListener('dragover', dragOver)

    return () => {
      document.removeEventListener('dragover', dragOver)
    }
  }, [])

  const upload = async (file: File) => {
    const attachment = {
      uuid: uuidv4(),
      url: '',
      mimetype: file.type,
      name: file.name,
      size: file.size,
      uploading: true,
      error: false,
      icon: getIconFromMimeType(file.type),
      isImage: file.type.startsWith('image'),
    }

    if (file.size > maxSize) {
      notify(
        intl.formatMessage(
          { id: 'form / upload / notification / max size' },
          { max: Math.round(maxSize / 1024) }
        ),
        'error'
      )
    } else {
      const formData = new FormData()

      onStart(attachment)

      formData.append('file', file)

      await fetch(host + path, {
        method: 'POST',
        body: formData,
        headers: {
          'X-AUTH-TOKEN': getToken(),
        },
      })
        .then(res => res.json())
        .then((resp: ApiDataType) => {
          if (resp.status == 'error') {
            switch (resp.reason) {
              case 'too_big':
                notify(
                  intl.formatMessage(
                    {
                      id: 'form / upload / notification / max size',
                    },
                    { max: Math.round(maxSize / 1024) }
                  ),
                  'error'
                )
                return

              // case 'other':
              //   return

              default:
                notify(`Error`, 'error')
                console.error(resp)
                return
            }
          } else {
            onEnd({
              ...attachment,
              url: resp.url,
              uploading: false,
            })
          }
        })
        .catch(error => {
          console.error(error)

          notify(
            intl.formatMessage({
              id: 'form / upload / notification / error',
            }),
            'error'
          )

          onEnd({
            ...attachment,
            uploading: false,
            error: true,
          })
        })
    }
  }

  useEffect(() => {
    if (
      !pasteElementRef ||
      !pasteElementRef.current ||
      !window.c.chatAttachments
    ) {
      return
    }

    const onPaste = (event: ClipboardEvent) => {
      const files = Array.from(event.clipboardData?.files || [])

      if (files[0]) {
        event.preventDefault()
        event.stopPropagation()

        upload(files[0])
      }
    }

    pasteElementRef.current.addEventListener('paste', onPaste)

    return () => {
      pasteElementRef.current?.removeEventListener('paste', onPaste)
    }
  }, [])

  const dropzoneProps = useDropzone({
    multiple: false,
    onDrop: files => {
      upload(files[0])
    },
  })

  return {
    ...dropzoneProps,
    isDraggingOverWindow,
  }
}

const mimeTypeToIcon = {
  // Image types
  'image/jpeg': 'fa-file-image',
  'image/png': 'fa-file-image',
  'image/gif': 'fa-file-image',
  'image/svg+xml': 'fa-file-image',
  'image/webp': 'fa-file-image',
  'image/bmp': 'fa-file-image',
  'image/vnd.microsoft.icon': 'fa-file-image',
  'image/tiff': 'fa-file-image',

  // Audio types
  'audio/midi': 'fa-file-audio',
  'audio/mpeg': 'fa-file-audio',
  'audio/webm': 'fa-file-audio',
  'audio/ogg': 'fa-file-audio',
  'audio/wav': 'fa-file-audio',

  // Video types
  'video/webm': 'fa-file-video',
  'video/ogg': 'fa-file-video',
  'video/mp4': 'fa-file-video',

  // Text types
  'text/css': 'fa-file-code',
  'text/html': 'fa-file-code',
  'text/javascript': 'fa-file-code',
  'text/plain': 'fa-file-alt',

  // Application types
  'application/pdf': 'fa-file-pdf',
  'application/zip': 'fa-file-zipper',
  'application/gzip': 'fa-file-zipper',
  'application/x-7z-compressed': 'fa-file-zipper',
  'application/x-bzip': 'fa-file-zipper',
  'application/x-bzip2': 'fa-file-zipper',
  'application/x-rar-compressed': 'fa-file-zipper',

  // Microsoft Word
  'application/msword': 'fa-file-word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'fa-file-word',

  // Microsoft Excel
  'application/vnd.ms-excel': 'fa-file-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    'fa-file-excel',

  //Microsoft Powerpoint
  'application/vnd.ms-powerpoint': 'fa-file-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'fa-file-powerpoint',

  // Otherwise
  default: 'fa-file',
}

const getIconFromMimeType = (mimeType: string) =>
  mimeTypeToIcon[mimeType] || mimeTypeToIcon['default']
